import React, { useState, useEffect } from 'react';
import { Search, ArrowLeft, ArrowRight, Clock, Tag } from 'react-feather';
import * as Icons from 'react-feather';
import AgentLayout from '../AgentLayout';
import { getCategoryBySlug, searchArticles } from './supportData';
import '../AgentDashboard.css';

const SupportCategory = ({
  categorySlug,
  agentInfo,
  onDashboard,
  onMyListings,
  onAddProperty,
  onLogout,
  onInvitations,
  onShowings,
  onConversations,
  onAvailableLeads,
  onTeamRoster,
  onSupportHub,
  onNavigateToHub,
  onNavigateToArticle
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [category, setCategory] = useState(null);
  const [isLightMode, setIsLightMode] = useState(true);

  useEffect(() => {
    const cat = getCategoryBySlug(categorySlug);
    setCategory(cat);
    if (cat) {
      setFilteredArticles(cat.articles);
    }
    
    // Detect theme
    const saved = localStorage.getItem('agent-theme');
    setIsLightMode(saved !== 'dark');
  }, [categorySlug]);

  // Filter articles based on search
  useEffect(() => {
    if (!category) return;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const filtered = category.articles.filter(article =>
        article.title.toLowerCase().includes(q) ||
        article.summary.toLowerCase().includes(q) ||
        article.tags.some(tag => tag.toLowerCase().includes(q))
      );
      setFilteredArticles(filtered);
    } else {
      setFilteredArticles(category.articles);
    }
  }, [searchQuery, category]);

  const handleArticleClick = (articleSlug) => {
    if (onNavigateToArticle) {
      onNavigateToArticle(categorySlug, articleSlug);
    }
  };

  const handleBackClick = () => {
    if (onNavigateToHub) {
      onNavigateToHub();
    }
  };

  const getIcon = (iconName) => {
    const IconComponent = Icons[iconName];
    return IconComponent ? <IconComponent size={24} /> : null;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (!category) {
    return (
      <AgentLayout
        activeTab="support"
        onDashboard={onDashboard}
        onMyListings={onMyListings}
        onAddProperty={onAddProperty}
        onInvitations={onInvitations}
        onShowings={onShowings}
        onConversations={onConversations}
        onAvailableLeads={onAvailableLeads}
        onTeamRoster={onTeamRoster}
        onSupportHub={onSupportHub}
        onLogout={onLogout}
      >
        <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>
          Category not found
        </div>
      </AgentLayout>
    );
  }

  const styles = {
    container: {
      padding: '40px',
      maxWidth: '1400px',
      margin: '0 auto',
      position: 'relative',
      zIndex: 10
    },
    headerCard: {
      backgroundColor: isLightMode ? '#ffffff' : 'rgba(255, 255, 255, 0.04)',
      border: isLightMode ? '1px solid rgba(26, 26, 26, 0.08)' : '1px solid rgba(255, 255, 255, 0.08)',
      borderRadius: '20px',
      padding: '32px',
      marginBottom: '32px',
      display: 'flex',
      alignItems: 'center',
      gap: '20px'
    },
    iconWrapper: {
      width: '64px',
      height: '64px',
      borderRadius: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    },
    headerInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px'
    },
    title: {
      fontSize: '1.75rem',
      fontWeight: '700',
      color: isLightMode ? '#1a1a1a' : '#ffffff',
      margin: 0
    },
    description: {
      fontSize: '0.95rem',
      color: isLightMode ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.6)',
      margin: 0
    },
    searchContainer: {
      marginBottom: '24px',
      position: 'relative'
    },
    searchIcon: {
      position: 'absolute',
      left: '20px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: isLightMode ? 'rgba(26, 26, 26, 0.4)' : 'rgba(255, 255, 255, 0.4)',
      pointerEvents: 'none'
    },
    searchInput: {
      width: '100%',
      padding: '16px 20px 16px 52px',
      fontSize: '1rem',
      backgroundColor: isLightMode ? '#ffffff' : 'rgba(255, 255, 255, 0.06)',
      border: isLightMode ? '1px solid rgba(26, 26, 26, 0.1)' : '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '14px',
      color: isLightMode ? '#1a1a1a' : '#ffffff',
      outline: 'none',
      transition: 'all 0.2s ease'
    },
    articleCount: {
      fontSize: '0.9rem',
      color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)',
      marginBottom: '16px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      fontWeight: '500'
    },
    articleList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px'
    },
    articleCard: {
      backgroundColor: isLightMode ? '#ffffff' : 'rgba(255, 255, 255, 0.04)',
      border: isLightMode ? '1px solid rgba(26, 26, 26, 0.08)' : '1px solid rgba(255, 255, 255, 0.08)',
      borderRadius: '16px',
      padding: '24px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '20px'
    },
    articleContent: {
      flex: 1,
      minWidth: 0,
      textAlign: 'left'
    },
    articleTitle: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: isLightMode ? '#1a1a1a' : '#ffffff',
      marginBottom: '8px',
      textAlign: 'left'
    },
    articleSummary: {
      fontSize: '0.9rem',
      color: isLightMode ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.55)',
      lineHeight: '1.5',
      marginBottom: '12px',
      textAlign: 'left'
    },
    articleMeta: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      flexWrap: 'wrap'
    },
    metaItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '0.8rem',
      color: isLightMode ? 'rgba(26, 26, 26, 0.4)' : 'rgba(255, 255, 255, 0.4)'
    },
    tags: {
      display: 'flex',
      gap: '6px',
      flexWrap: 'wrap'
    },
    tag: {
      padding: '4px 10px',
      backgroundColor: isLightMode ? 'rgba(26, 26, 26, 0.06)' : 'rgba(255, 255, 255, 0.06)',
      borderRadius: '6px',
      fontSize: '0.75rem',
      color: isLightMode ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.5)'
    },
    arrowIcon: {
      color: isLightMode ? 'rgba(26, 26, 26, 0.25)' : 'rgba(255, 255, 255, 0.25)',
      flexShrink: 0,
      transition: 'all 0.2s ease'
    },
    noResults: {
      textAlign: 'center',
      padding: '48px 24px',
      color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)'
    },
    noResultsTitle: {
      fontSize: '1.1rem',
      marginBottom: '8px',
      color: isLightMode ? 'rgba(26, 26, 26, 0.7)' : 'rgba(255, 255, 255, 0.7)'
    }
  };

  const categoryContent = (
    <div style={styles.container}>
      {/* Back Button */}
      <div style={{ textAlign: 'left', marginBottom: '32px' }}>
        <button
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            color: isLightMode ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.6)',
            fontSize: '0.9rem',
            cursor: 'pointer',
            padding: '10px 16px',
            borderRadius: '10px',
            transition: 'all 0.2s ease',
            border: isLightMode ? '1px solid rgba(26, 26, 26, 0.1)' : '1px solid rgba(255, 255, 255, 0.1)',
            background: isLightMode ? '#ffffff' : 'transparent'
          }}
          onClick={handleBackClick}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = isLightMode ? 'rgba(26, 26, 26, 0.2)' : 'rgba(255, 255, 255, 0.2)';
            e.currentTarget.style.color = isLightMode ? '#1a1a1a' : '#ffffff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = isLightMode ? 'rgba(26, 26, 26, 0.1)' : 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.color = isLightMode ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.6)';
          }}
        >
          <ArrowLeft size={18} />
          Help Center
        </button>
      </div>

      {/* Header Card */}
      <div style={styles.headerCard}>
        <div
          style={{
            ...styles.iconWrapper,
            backgroundColor: `${category.color}20`,
            color: category.color
          }}
        >
          {getIcon(category.icon)}
        </div>
        <div style={styles.headerInfo}>
          <h1 style={styles.title}>{category.title}</h1>
          <p style={styles.description}>{category.description}</p>
        </div>
      </div>

      {/* Search */}
      <div style={styles.searchContainer}>
        <Search size={18} style={styles.searchIcon} />
        <input
          type="text"
          placeholder={`Search in ${category.title}...`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
          onFocus={(e) => {
            e.target.style.borderColor = `${category.color}50`;
            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.06)';
          }}
        />
      </div>

      {/* Article Count */}
      <div style={styles.articleCount}>
        {filteredArticles.length} article{filteredArticles.length !== 1 ? 's' : ''}
        {searchQuery && ` matching "${searchQuery}"`}
      </div>

      {/* Article List */}
      {filteredArticles.length > 0 ? (
        <div style={styles.articleList}>
          {filteredArticles.map((article) => (
            <div
              key={article.slug}
              style={styles.articleCard}
              onClick={() => handleArticleClick(article.slug)}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = `${category.color}40`;
                e.currentTarget.style.transform = 'translateX(4px)';
                e.currentTarget.style.boxShadow = isLightMode ? '0 8px 24px rgba(0, 0, 0, 0.06)' : '0 8px 24px rgba(0, 0, 0, 0.2)';
                const arrow = e.currentTarget.querySelector('.article-arrow');
                if (arrow) {
                  arrow.style.color = category.color;
                  arrow.style.transform = 'translateX(4px)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = isLightMode ? 'rgba(26, 26, 26, 0.08)' : 'rgba(255, 255, 255, 0.08)';
                e.currentTarget.style.transform = 'translateX(0)';
                e.currentTarget.style.boxShadow = 'none';
                const arrow = e.currentTarget.querySelector('.article-arrow');
                if (arrow) {
                  arrow.style.color = isLightMode ? 'rgba(26, 26, 26, 0.25)' : 'rgba(255, 255, 255, 0.25)';
                  arrow.style.transform = 'translateX(0)';
                }
              }}
            >
              <div style={styles.articleContent}>
                <div style={styles.articleTitle}>{article.title}</div>
                <div style={styles.articleSummary}>{article.summary}</div>
                <div style={styles.articleMeta}>
                  <div style={styles.metaItem}>
                    <Clock size={14} />
                    {article.readTime} min read
                  </div>
                  <div style={styles.metaItem}>
                    Updated {formatDate(article.updatedAt)}
                  </div>
                  <div style={styles.tags}>
                    {article.tags.slice(0, 3).map((tag) => (
                      <span key={tag} style={styles.tag}>{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
              <ArrowRight size={20} className="article-arrow" style={styles.arrowIcon} />
            </div>
          ))}
        </div>
      ) : (
        <div style={styles.noResults}>
          <div style={styles.noResultsTitle}>No articles found</div>
          <p>Try adjusting your search terms</p>
        </div>
      )}
    </div>
  );

  return (
    <AgentLayout
      activeTab="support"
      onDashboard={onDashboard}
      onMyListings={onMyListings}
      onAddProperty={onAddProperty}
      onInvitations={onInvitations}
      onShowings={onShowings}
      onConversations={onConversations}
      onAvailableLeads={onAvailableLeads}
      onTeamRoster={onTeamRoster}
      onSupportHub={onSupportHub}
      onLogout={onLogout}
    >
      {categoryContent}
    </AgentLayout>
  );
};

export default SupportCategory;